import { Component } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ApplicationListModel } from '../../../../core/models/customer/application-list.model';
import { ApplicationCode } from '../../../../../enums/application-code.enum';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SurveyService } from '../../../../core/services/survey/survey.service';
import { CommonModule } from '@angular/common';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { AnswerTypeUtils } from '../../../../utils/answer-type-utils';
import { QuestionDetails } from '../../../../core/models/survey/question-details.model';
import { ToastService } from '../../../../core/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-create-edit-survey',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FullscreenOverlayComponent,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './create-edit-survey.component.html',
  styleUrls: ['./create-edit-survey.component.scss'],
})
export class CreateEditSurveyComponent {
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private surveyService: SurveyService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.saveSurveyForm = this.fb.group({
      name: ['', Validators.required],
      hasDeviceUtilization: [null, Validators.required],
      questions: this.fb.array([this.createQuestionFormGroup()]),
    });
  }

  saveSurveyForm: FormGroup;
  isOverlayLoading: boolean = false;
  answerTypes: { id: number; name: string }[] = [];
  applications: ApplicationListModel[] = [
    {
      id: 1,
      name: 'Dermatology',
      code: 'DERM',
      applicationCode: ApplicationCode.DERM,
    },
    {
      id: 2,
      name: 'Osas',
      code: 'OSAS',
      applicationCode: ApplicationCode.OSAS,
    },
    {
      id: 3,
      name: 'Diabet',
      code: 'DIAB',
      applicationCode: ApplicationCode.DIAB,
    },
  ];
  surveyCode: string = '';
  questionOptionId: string | null = '';
  questionAddOptionId: string | null = '';
  editApplicationCode: number = 0;
  questionDetails: QuestionDetails | null = null;

  ngOnInit(): void {
    this.surveyCode = this.route.snapshot.paramMap.get('code')!;
    this.answerTypes = Object.values(AnswerTypeUtils['ANSWERTYPE']);
    const type = this.route.snapshot.url[0]?.path;
    if (type === 'add') {
      this.questionAddOptionId = this.route.snapshot.paramMap.get('id');
    } else if (type === 'edit') {
      this.questionOptionId = this.route.snapshot.paramMap.get('id');
    }
    if (this.questionOptionId) {
      this.surveyService.getQuestionDetail(this.questionOptionId).subscribe({
        next: (res) => {
          this.questionDetails = res;
          this.saveSurveyForm = this.fb.group({
            questions: this.fb.array([this.createQuestionFormGroup()]),
          });
        },
      });
    }
  }

  get applicationTitle(): string | undefined {
    return (
      this.applications.find((app: any) => app.code === this.surveyCode)
        ?.name ||
      this.applications.find(
        (app: any) => app.applicationCode === this.editApplicationCode
      )?.name
    );
  }

  createQuestionFormGroup(): FormGroup {
    const create = this.fb.group({
      text: ['', Validators.required],
      answerType: [0, Validators.required],
      answerOptions: this.fb.array([this.createAnswerOptionFormGroup()]),
    });

    const edit = this.fb.group({
      text: [this.questionDetails?.text || '', Validators.required],
      answerType: [this.questionDetails?.answerType || 0, Validators.required],
      answerOptions: this.fb.array(
        this.questionDetails?.answerOptions?.map((option: any) =>
          this.createAnswerOptionFormGroup(option)
        ) || []
      ),
    });

    return this.surveyCode ? create : edit;
  }

  createAnswerOptionFormGroup(optionData?: any): FormGroup {
    const create = this.fb.group({
      answerText: ['', Validators.required],
      score: ['', Validators.required],
    });

    const edit = this.fb.group({
      answerText: [optionData?.answerText || '', Validators.required],
      score: [optionData?.score || 0, Validators.required],
    });

    return optionData ? edit : create;
  }

  onSubmit(): void {
    this.isOverlayLoading = true;
    // Create survey
    if (this.surveyCode) {
      const formValue = this.saveSurveyForm.value;
      const code = this.applications.find(
        (application: any) => application.code === this.surveyCode
      );

      const formData = {
        name: formValue.name,
        hasDeviceUtilization: formValue.hasDeviceUtilization === 'true',
        applicationCode: code?.applicationCode,
        questions: !this.checkDeviceUtilization
          ? formValue.questions.map((question: any) => ({
              text: question.text,
              answerType: Number(question.answerType),
              answerOptions:
                question.answerType == 1
                  ? question.answerOptions.map((option: any) => ({
                      answerText: option.answerText,
                      score: option.score,
                    }))
                  : null,
            }))
          : [],
      };

      this.surveyService.createSurvey(formData).subscribe({
        next: () => {
          this.toastService.showToast(
            this.translate.instant('surveyCreatedSuccessfully'),
            'success'
          );
        },
        complete: () => {
          setTimeout(() => {
            this.isOverlayLoading = false;
            this.router.navigate(['/survey/list']);
          }, 2000);
        },
      });
    }
    // Edit Question
    else if (this.questionOptionId) {
      const formValue = this.saveSurveyForm.value;
      const question = formValue?.questions[0];

      const formData = {
        id: this.questionOptionId,
        text: question.text,
        answerType: Number(question.answerType),
        answerOptions:
          question.answerType == 1
            ? question.answerOptions.map((option: any) => ({
                answerText: option.answerText,
                score: option.score,
              }))
            : null,
      };

      this.surveyService
        .updateQuestion(this.questionOptionId, formData)
        .subscribe({
          next: () => {
            this.toastService.showToast(
              this.translate.instant('questionEditedSuccessfully'),
              'success'
            );
          },
          complete: () => {
            setTimeout(() => {
              this.isOverlayLoading = false;
              this.router.navigate(['/survey/list']);
            }, 2000);
          },
        });
    }
    // Add Question
    else if (this.questionAddOptionId) {
      const formValue = this.saveSurveyForm.value;

      const formData = {
        surveyId: this.questionAddOptionId,
        questions: formValue.questions.map((question: any) => ({
          text: question.text,
          answerType: Number(question.answerType),
          answerOptions:
            question.answerType == 1
              ? question.answerOptions.map((option: any) => ({
                  answerText: option.answerText,
                  score: option.score,
                }))
              : null,
        })),
      };

      this.surveyService
        .addQuestion(this.questionAddOptionId, formData)
        .subscribe({
          next: () => {
            this.toastService.showToast(
              this.translate.instant('questionAddedSuccessfully'),
              'success'
            );
          },
          complete: () => {
            setTimeout(() => {
              this.isOverlayLoading = false;
              this.router.navigate(['/survey/list']);
            }, 2000);
          },
        });
    }
  }

  get questions(): FormArray {
    return this.saveSurveyForm.get('questions') as FormArray;
  }

  get checkDeviceUtilization(): boolean {
    return this.saveSurveyForm.get('hasDeviceUtilization')?.value == 'true';
  }

  getAnswerOptions(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('answerOptions') as FormArray;
  }

  addQuestion(): void {
    this.questions.push(this.createQuestionFormGroup());
  }

  removeQuestion(index: number): void {
    if (index >= 0 && index < this.questions.length) {
      this.questions.removeAt(index);
    }
  }

  addAnswerOption(questionIndex: number): void {
    this.getAnswerOptions(questionIndex).push(
      this.createAnswerOptionFormGroup()
    );
  }

  removeAnswerOption(answerIndex: number, optionIndex: number): void {
    const answerOptions = this.getAnswerOptions(answerIndex);
    if (answerOptions && optionIndex < answerOptions.length) {
      answerOptions.removeAt(optionIndex);
    }
  }
}
